/** Admin form helpers: DTO builders, hydration, validation. */
import { getAdminFormDefaultRoleLabel, getAdminRoleCategory } from './admin-configs';
import { getRoleLabel, roleKind, roleMatches, roleMode } from '@/utils/role-label-utils';
import type {
  AdminFormFlags,
  CreateAdminDto,
  CreateAdminFormState,
  UpdateAdminDto,
} from '@/types/admin.types';
import { normalizeStaffAccountStatus, type ContactKind } from '@/types/user.types';
import {
  phoneToE164,
  parseStoredPhone,
  resolveStoredContact,
  nextContactFields,
  primaryContactKindFromType,
  assignPrimaryContactFields,
} from '@/utils/user-contact-utils';
import { formatPersonName as formatSharedPersonName } from '@/utils/common-utils';

/** Role id, code, mode, and label. */
type RoleRef = { id: string; code: string; mode?: string | null; label?: string };

/** Flags that drive which admin detail sections render. */
type AdminDetailVisibility = {
  isAdminSuperRole: boolean;
  isCoordinatorCategory: boolean;
  isManagerCategory: boolean;
  isRecruiterCategory: boolean;
  hasInPersonRole: boolean;
  hasRemoteRole: boolean;
  showResidence: boolean;
  showEstablishments: boolean;
  showRemoteDepts: boolean;
  showProspectionGoal: boolean;
};

/** Inputs for the admin edit validation schema payload. */
type AdminEditValidationPayloadParams = {
  form: UpdateAdminDto;
  primaryContact: string;
  primaryContactType: 'email' | 'phone';
  primaryContactCountryCode: string;
  primaryContactExists: boolean | null | undefined;
  requiresResidenceDepartments: boolean;
  contactExists: boolean | null | undefined;
  phoneCountryCode: string;
};

/** First + last, then email, then id / fallback. */
export function formatPersonName(
  person: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    id?: string;
  },
  fallback = '',
): string {
  return formatSharedPersonName(person, fallback);
}

/** Empty create form with the default phone country. */
export function emptyCreateAdminForm(defaultCountry: string): CreateAdminFormState {
  return {
    contact: '',
    contactType: 'unknown',
    contactCountryCode: defaultCountry,
    secondaryContact: '',
    secondaryContactCountryCode: defaultCountry,
    firstName: '',
    lastName: '',
    roleIds: [],
    establishmentIds: [],
    managedById: undefined,
    annualProspectionGoal: '',
    residenceDepartmentIds: [],
    departments: [],
    programIds: [],
    status: 'active',
  };
}

/** Patch one admin-form field. */
export function applyAdminFormField<T extends object>(
  formData: T,
  field: keyof T,
  value: unknown,
): T {
  return { ...formData, [field]: value };
}

/** Role codes for the selected role ids. */
export function getSelectedRoleCodes(roleIds: string[], roles: RoleRef[]): string[] {
  if (roleIds.length === 0) return [];
  return roles.filter((role) => roleIds.includes(role.id)).map((role) => role.code);
}

/** Role objects for the selected role ids. */
export function getSelectedRolesData<T extends { id: string }>(roleIds: string[], roles: T[]): T[] {
  return roles.filter((role) => roleIds.includes(role.id));
}

/** Role codes shown in the edit form (fallback to the admin record). */
export function resolveEditSelectedRoleCodes(
  formRoleIds: string[] | undefined,
  roles: RoleRef[],
  admin?: { userRoles?: Array<{ role: { code: string } }> },
): string[] {
  if (formRoleIds && formRoleIds.length > 0) return getSelectedRoleCodes(formRoleIds, roles);
  return admin?.userRoles?.map((item) => item.role.code) ?? [];
}

/** Fallback in-person flag from the admin record. */
export function fallbackHasInPersonFromAdmin(admin?: {
  userRoles?: Array<{ role: { code: string } }>;
}): boolean {
  return (
    admin?.userRoles?.some((item) => {
      const mode = roleMode(item.role.code);
      return mode !== 'remote' && mode !== 'hybrid' && roleKind(item.role.code) !== 'admin';
    }) ?? false
  );
}

/** Fallback remote flag from the admin record. */
export function fallbackHasRemoteFromAdmin(admin?: {
  userRoles?: Array<{ role: { code: string } }>;
}): boolean {
  return (
    admin?.userRoles?.some((item) => {
      const mode = roleMode(item.role.code);
      return mode === 'remote' || mode === 'hybrid';
    }) ?? false
  );
}

/** Derive mode / residence / single-role flags from selected roles. */
export function deriveAdminFormFlags(params: {
  selectedRoleCodes: string[];
  selectedRolesData: Array<{ mode?: string | null }>;
  showDepartmentForRecruiter?: boolean;
  fallbackHasInPerson?: boolean;
  fallbackHasRemote?: boolean;
}): AdminFormFlags {
  const isAdminRole = params.selectedRoleCodes.includes('admin');
  const categories = new Set(params.selectedRoleCodes.map(getAdminRoleCategory));

  let hasInPersonMode = false;
  let hasRemoteMode = false;
  if (!isAdminRole) {
    if (params.selectedRolesData.length > 0) {
      hasInPersonMode = params.selectedRolesData.some(
        (role) => role.mode === 'in_person' || role.mode === 'hybrid',
      );
      hasRemoteMode = params.selectedRolesData.some(
        (role) => role.mode === 'remote' || role.mode === 'hybrid',
      );
    } else {
      hasInPersonMode = params.fallbackHasInPerson ?? false;
      hasRemoteMode = params.fallbackHasRemote ?? false;
    }
  }

  const isManagerCategory = categories.has('manager');
  const isCoordinatorCategory = categories.has('coordinator');
  const isRecruiterCategory = categories.has('recruiter');
  const departmentCategories = params.showDepartmentForRecruiter
    ? isManagerCategory || isCoordinatorCategory || isRecruiterCategory
    : isManagerCategory || isCoordinatorCategory;

  return {
    isAdminRole,
    hasInPersonMode,
    hasRemoteMode,
    isManagerCategory,
    isCoordinatorCategory,
    isRecruiterCategory,
    isCoordinatorRole: hasInPersonMode && isCoordinatorCategory,
    isCoordinatorRemoteRole: hasRemoteMode && isCoordinatorCategory,
    requiresResidenceDepartments: hasInPersonMode && (isManagerCategory || isCoordinatorCategory),
    showDepartmentField: hasRemoteMode && departmentCategories,
    showProgramField: hasRemoteMode && isCoordinatorCategory,
  };
}

/** Which detail sections to show from the admin's roles. */
export function deriveAdminDetailVisibility(admin: {
  userRoles?: Array<{ role?: { code?: string } }>;
}): AdminDetailVisibility {
  const isAdminSuperRole =
    admin.userRoles?.some((ur) => roleKind(ur.role?.code) === 'admin') ?? false;

  const roleCodes = admin.userRoles?.map((ur) => ur.role?.code?.toLowerCase() ?? '') ?? [];
  const baseCategorySet = new Set(roleCodes.map(getAdminRoleCategory));

  const isCoordinatorCategory = baseCategorySet.has('coordinator');
  const isManagerCategory = baseCategorySet.has('manager');
  const isRecruiterCategory = baseCategorySet.has('recruiter');

  // Has an inperson-capable role (mode is not remote/hybrid; admin is excluded)
  const hasInPersonRole =
    !isAdminSuperRole &&
    roleCodes.some((c) => {
      const mode = roleMode(c);
      return mode !== 'remote' && mode !== 'hybrid' && roleKind(c) !== 'admin';
    });

  // Has a remote-capable role
  const hasRemoteRole =
    !isAdminSuperRole &&
    roleCodes.some((c) => {
      const mode = roleMode(c);
      return mode === 'remote' || mode === 'hybrid';
    });

  return {
    isAdminSuperRole,
    isCoordinatorCategory,
    isManagerCategory,
    isRecruiterCategory,
    hasInPersonRole,
    hasRemoteRole,
    showResidence: hasInPersonRole && (isManagerCategory || isCoordinatorCategory),
    showEstablishments: hasInPersonRole && (isManagerCategory || isCoordinatorCategory),
    showRemoteDepts:
      hasRemoteRole && (isManagerCategory || isCoordinatorCategory || isRecruiterCategory),
    showProspectionGoal: isCoordinatorCategory,
  };
}

/** Validator extras: contact aliases or residence-department flag. */
export function createAdminFieldValidationPayload(
  field: string,
  updatedData: CreateAdminFormState,
  contactType: ContactKind,
  phoneCountryCode: string,
  roles: RoleRef[],
): CreateAdminFormState & Record<string, unknown> {
  if (field === 'contact') {
    return {
      ...updatedData,
      _contactType: contactType,
      _phoneCountryCode: phoneCountryCode,
    };
  }
  return {
    ...updatedData,
    _requiresResidenceDepartments: rolesRequireResidenceDepartments(roles, updatedData.roleIds),
  };
}

/** Whether selected roles require residence departments. */
export function rolesRequireResidenceDepartments(roles: RoleRef[], roleIds: string[]): boolean {
  return roles
    .filter((role) => roleIds.includes(role.id))
    .some((role) => {
      const kind = roleKind(role.code);
      return (kind === 'coordinator' || kind === 'manager') && roleMode(role.code) !== 'remote';
    });
}

/** Department options keyed by id. */
export function mapDepartmentIdOptions(
  departments: Array<{ id: string; code: string; name: string }>,
): Array<{ value: string; label: string }> {
  return departments.map((dept) => ({
    value: dept.id,
    label: `${dept.code} — ${dept.name}`,
  }));
}

/** Department options keyed by code. */
export function mapDepartmentCodeOptions(
  departments: Array<{ code: string; name: string }>,
): Array<{ value: string; label: string }> {
  return departments.map((dept) => ({
    value: dept.code,
    label: `${dept.code} - ${dept.name}`,
  }));
}

/** Department codes → `code - name` labels (unknown codes stay as-is). */
export function mapAdminDepartmentCodeLabels(
  codes: string[] | undefined,
  departments: Array<{ code: string; name: string }>,
): string[] {
  return (codes ?? []).map((code) => {
    const dept = departments.find((d) => d.code === code);
    return dept ? `${dept.code} - ${dept.name}` : code;
  });
}

/** Program select options. */
export function mapProgramSelectOptions(
  programs: Array<{ id: string; name?: string }>,
): Array<{ value: string; label: string }> {
  return programs.map((program) => ({
    value: program.id,
    label: program.name ?? program.id,
  }));
}

/** Establishment options scoped to the actor. */
export function mapScopedEstablishmentOptions(
  establishments: Array<{ id: string; name?: string | null; uaiCode?: string | null }>,
): Array<{ value: string; label: string }> {
  return establishments
    .filter((establishment) => establishment != null)
    .map((establishment) => {
      const uaiSuffix = establishment.uaiCode ? ` (${establishment.uaiCode})` : '';
      return {
        value: establishment.id,
        label: `${establishment.name ?? ''}${uaiSuffix}`,
      };
    });
}

/** Residence department codes from the form. */
export function getResidenceDepartmentCodes(
  residenceDepartmentIds: string[],
  departments: Array<{ id: string; code: string }>,
): string[] {
  if (!residenceDepartmentIds.length) return [];
  return departments
    .filter((dept) => residenceDepartmentIds.includes(dept.id))
    .map((dept) => dept.code)
    .filter(Boolean);
}

/** User id used to scope establishments on create. */
export function resolveCreateEstablishmentSourceUserId(params: {
  isCoordinatorRole: boolean;
  currentUserRoleCode?: string;
  currentUserId?: string;
}): string | undefined {
  if (params.isCoordinatorRole) return undefined;
  if (
    roleKind(params.currentUserRoleCode) === 'manager' &&
    roleMode(params.currentUserRoleCode) !== 'remote'
  ) {
    return params.currentUserId;
  }
  return undefined;
}

/** User id used to scope establishments on edit. */
export function resolveEditEstablishmentSourceUserId(params: {
  isCoordinatorRole: boolean;
  currentUserRoleCode?: string;
  currentUserId?: string;
}): string | undefined {
  if (
    roleKind(params.currentUserRoleCode) === 'manager' &&
    roleMode(params.currentUserRoleCode) !== 'remote' &&
    params.isCoordinatorRole
  ) {
    return params.currentUserId;
  }
  return undefined;
}

/** Keep only establishments still valid after a department change. */
export function nextEstablishmentIdsAfterDeptChange(
  prevIds: string[],
  selectedResidenceDepartmentCodes: string[],
  assignableEstablishments: Array<{ id: string }>,
): string[] | null {
  if (prevIds.length === 0) return null;
  if (selectedResidenceDepartmentCodes.length === 0) return [];
  const allowed = new Set(assignableEstablishments.map((item) => item.id));
  const nextIds = prevIds.filter((id) => allowed.has(id));
  if (nextIds.length === prevIds.length) return null;
  return nextIds;
}

/** Ensure the current manager stays in the select options. */
export function withCurrentManagerOption(
  options: Array<{ value: string; label: string }>,
  currentManagedById: string | undefined,
  currentManagerData:
    | {
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | undefined,
): Array<{ value: string; label: string }> {
  if (!currentManagedById || !currentManagerData) return options;
  if (options.some((option) => option.value === currentManagedById)) return options;
  return [
    { value: currentManagerData.id, label: formatPersonName(currentManagerData) },
    ...options,
  ];
}

/** Next email/phone fields after a contact-type change. */
export function nextContactState(
  prev: CreateAdminFormState,
  value: string,
  type: ContactKind,
  countryCode: string | undefined,
  defaultCountry: string,
): CreateAdminFormState {
  const next = nextContactFields({
    previousType: prev.contactType,
    value,
    type,
    countryCode,
    previousCountryCode: prev.contactCountryCode,
    defaultCountry,
    previousSecondary: prev.secondaryContact,
    previousSecondaryCountry: prev.secondaryContactCountryCode,
  });
  return {
    ...prev,
    ...next,
    contactType: next.contactType as ContactKind,
    contactCountryCode: next.contactCountryCode ?? prev.contactCountryCode,
  };
}

/** Helper text under the roles picker. */
export function getCreateRolesHelperText(isSingleRoleMode: boolean, initialRole?: string): string {
  if (isSingleRoleMode || initialRole) return 'Rôle automatiquement assigné selon votre profil';
  return `Sélectionnez les rôles administratifs (${getRoleLabel('admin')}, ${getRoleLabel('coordinator')}, ${getRoleLabel('manager')})`;
}

/** Success toast after create/edit. */
export function formatAdminMutationSuccess(
  roleLabel: string | undefined,
  mode: 'create' | 'edit',
): string {
  const label = roleLabel || getAdminFormDefaultRoleLabel();
  if (mode === 'create') return `${label} créé avec succès !`;
  return `${label} modifié avec succès !`;
}

/** Role label for toasts / titles. */
export function resolveRoleLabel(
  roles: RoleRef[],
  roleId: string | undefined,
  fallback?: string,
): string {
  return (
    roles.find((role) => role.id === roleId)?.label || fallback || getAdminFormDefaultRoleLabel()
  );
}

/** E.164 phone from primary or secondary contact. */
function resolveCreateAdminPhone(form: CreateAdminFormState): string | undefined {
  if (form.contactType === 'phone') {
    return phoneToE164(form.contact, form.contactCountryCode);
  }
  if (form.secondaryContact?.trim()) {
    return phoneToE164(form.secondaryContact, form.secondaryContactCountryCode);
  }
  return undefined;
}

/** Build create-admin DTO from the form. */
export function buildCreateAdminDto(params: {
  form: CreateAdminFormState;
  requiresResidenceDepartments: boolean;
  isCoordinatorRole: boolean;
  currentUserRoleCode?: string;
}): CreateAdminDto {
  const { form } = params;
  return {
    inviteBy: form.contactType === 'email' ? 'email' : 'phone',
    primaryContactKind: primaryContactKindFromType(form.contactType),
    email: form.contactType === 'email' ? form.contact : form.secondaryContact?.trim() || undefined,
    firstName: form.firstName,
    lastName: form.lastName,
    phone: resolveCreateAdminPhone(form),
    roleIds: form.roleIds,
    establishmentIds: form.establishmentIds.length > 0 ? form.establishmentIds : undefined,
    managedById: form.managedById,
    annualProspectionGoal:
      params.isCoordinatorRole &&
      params.currentUserRoleCode === 'admin' &&
      form.annualProspectionGoal !== ''
        ? Number(form.annualProspectionGoal)
        : undefined,
    residenceDepartmentIds: params.requiresResidenceDepartments
      ? form.residenceDepartmentIds
      : undefined,
    departments: form.departments.length > 0 ? form.departments : undefined,
    programIds: form.programIds.length > 0 ? form.programIds : undefined,
  };
}

/** Coordinator prospection goal, only when the current user is an admin. */
function resolveAnnualProspectionGoal(
  isCoordinatorRole: boolean,
  currentUserRoleCode: string | undefined,
  value: unknown,
): number | undefined {
  if (!isCoordinatorRole || currentUserRoleCode !== 'admin' || value == null) return undefined;
  return Number(value);
}

/** Return `ids` when non-empty, else `undefined` (omit from PATCH). */
function nonemptyIds(ids?: string[]): string[] | undefined {
  if (!ids?.length) return undefined;
  return ids;
}

/** Return `ids` when non-empty, else `[]`. */
function idsOrEmpty(ids?: string[]): string[] {
  return ids?.length ? ids : [];
}

/** Write the typed primary contact onto email or phone and set `primaryContactKind`. */
export function applyAdminPrimaryContactChange(
  form: UpdateAdminDto,
  value: string,
  type: 'email' | 'phone',
): UpdateAdminDto {
  return {
    ...form,
    email: type === 'email' ? value : undefined,
    phone: type === 'phone' ? value : form.phone,
    primaryContactKind: type,
  };
}

/** Flatten edit-form state into the admin edit validation schema payload. */
export function adminEditValidationPayload(
  params: AdminEditValidationPayloadParams,
): Record<string, unknown> {
  return {
    ...params.form,
    contact: params.primaryContact,
    _contactType: params.primaryContactType,
    contactCountryCode: params.primaryContactCountryCode,
    _phoneCountryCode:
      params.primaryContactType === 'email'
        ? params.phoneCountryCode
        : params.primaryContactCountryCode,
    _primaryContactExists: params.primaryContactExists,
    _requiresResidenceDepartments: params.requiresResidenceDepartments,
    _contactExists: params.contactExists,
  };
}

/** Build update-admin DTO from the form. */
export function buildUpdateAdminDto(params: {
  form: UpdateAdminDto;
  primaryContact: string;
  primaryContactType: 'email' | 'phone';
  primaryContactCountryCode: string;
  secondaryContact?: string;
  secondaryContactCountryCode?: string;
  defaultCountry: string;
  requiresResidenceDepartments: boolean;
  isCoordinatorRole: boolean;
  showProgramField: boolean;
  currentUserRoleCode?: string;
}): UpdateAdminDto {
  const { form } = params;
  const dto: UpdateAdminDto = {
    firstName: form.firstName,
    lastName: form.lastName,
    status: form.status,
    roleIds: nonemptyIds(form.roleIds),
    managedById: form.managedById || undefined,
    establishmentIds: nonemptyIds(form.establishmentIds),
    annualProspectionGoal: resolveAnnualProspectionGoal(
      params.isCoordinatorRole,
      params.currentUserRoleCode,
      form.annualProspectionGoal,
    ),
    residenceDepartmentIds: params.requiresResidenceDepartments
      ? form.residenceDepartmentIds
      : undefined,
    departments: idsOrEmpty(form.departments),
    programIds: params.showProgramField ? (form.programIds ?? []) : undefined,
  };
  assignPrimaryContactFields({
    dto,
    primaryValue: params.primaryContact,
    primaryKind: params.primaryContactType,
    primaryCountry: params.primaryContactCountryCode,
    secondaryValue: params.secondaryContact,
    secondaryCountry: params.secondaryContactCountryCode,
    defaultCountry: params.defaultCountry,
  });
  return dto;
}

/** Hydrate the edit form from an admin record. */
export function adminToEditFormData(
  admin: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    primaryContactKind?: 'email' | 'phone' | null;
    status?: string;
    managedById?: string | null;
    programIds?: string[];
    userRoles?: Array<{ roleId: string }>;
    userEstablishments?: Array<{
      establishmentId: string;
      establishment?: { id?: string; name?: string };
    }>;
    userProfiles?: Array<{
      annualProspectionGoal?: number | null;
      residenceDepartmentIds?: string[];
      residenceDepartments?: Array<{ id: string }>;
      departments?: string[];
    }>;
  },
  defaultCountry: string,
): {
  formData: UpdateAdminDto;
  phoneCountryCode: string;
  labelEntries: Array<{ id: string; name: string }>;
} {
  const stored = resolveStoredContact(
    admin.email ?? null,
    admin.phone ?? null,
    admin.primaryContactKind,
  );
  const secondaryPhone =
    stored.mode === 'email' && admin.phone ? parseStoredPhone(admin.phone, defaultCountry) : null;
  const phoneCountryCode =
    stored.mode === 'phone' ? stored.isoCode : (secondaryPhone?.isoCode ?? defaultCountry);

  return {
    formData: {
      firstName: admin.firstName || '',
      lastName: admin.lastName || '',
      primaryContactKind: stored.mode,
      phone: stored.mode === 'phone' ? stored.displayValue : (secondaryPhone?.displayValue ?? ''),
      status: normalizeStaffAccountStatus(String(admin.status)),
      roleIds: admin.userRoles?.map((item) => item.roleId) || [],
      managedById: admin.managedById ?? '',
      annualProspectionGoal: admin.userProfiles?.[0]?.annualProspectionGoal ?? undefined,
      residenceDepartmentIds:
        admin.userProfiles?.[0]?.residenceDepartmentIds ??
        admin.userProfiles?.[0]?.residenceDepartments?.map((dept) => dept.id) ??
        [],
      establishmentIds: admin.userEstablishments?.map((item) => item.establishmentId) || [],
      departments: admin.userProfiles?.[0]?.departments || [],
      programIds: admin.programIds || [],
    },
    phoneCountryCode,
    labelEntries: (admin.userEstablishments ?? [])
      .filter((item) => item.establishment?.id && item.establishment?.name)
      .map((item) => ({
        id: item.establishment!.id!,
        name: item.establishment!.name!,
      })),
  };
}

/** Helper under the in-person manager picker. */
export function createAdminManagerHelperText(roleCode: string | undefined): string {
  if (roleKind(roleCode) === 'manager' && roleMode(roleCode) !== 'remote') {
    return 'Vous serez automatiquement le manager de ce coordinateur';
  }
  return 'Le manager qui supervisera ce coordinateur';
}

/** Helper under the remote manager picker. */
export function createAdminRemoteManagerHelperText(roleCode: string | undefined): string {
  if (roleMatches(roleCode, 'manager', 'remote')) {
    return 'Vous serez automatiquement le gestionnaire de ce coordinateur';
  }
  return 'Le gestionnaire qui supervisera ce coordinateur';
}

/** Placeholder for the remote manager picker. */
export function createAdminRemoteManagerPlaceholder(roleCode: string | undefined): string {
  if (roleMatches(roleCode, 'manager', 'remote')) {
    return 'Sélectionner un gestionnaire (vous par défaut)';
  }
  return 'Rechercher un gestionnaire...';
}
